/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {entityId} from '../services/ConnectUtils';
import {CTCEntity, Entity, Role} from '../types/entities';

//CACTC Demo Super Admin Role ids for CACTC Application across EU, US & QA env.
export const SuperAdminDemoRoleIds = [7109, 7146, 47408];

/**
 * Validator Params
 */
export type ValidatorParams = {
  entity: CTCEntity,
  type: Entity,
  role: Role,
  label: string
}

/**
 * LabelRulesValidator API is used to define the rules validator to return an entity label.
 * The EntityLabel checks the user's role at initialization of the app.
 *
 * If no custom rules label validator is set, defaultRuleValidator is utilized.
 *
 * ```typescript
 * import {LabelRulesValidator} from '@ion/core/src/entities/LabelRulesValidator';
 *
 * class EntityLabel {
 * ...
 *  get = (entity: CTCEntity | Link, validator?: LabelRulesValidator) => {
 * ...
 * }
 * ```
 * @params ValidatorParams
 */
export type LabelRulesValidator = (params: ValidatorParams) => string;

/**
 * Checks if role is restricted in order to return a resolved label.
 */
export const isRestricted = (role: Role): boolean => {
  return SuperAdminDemoRoleIds.includes(role?.id);
};

/**
 * Defines a mapping of `Entity` types and their label resolvers.
 */
const resolvers = {
  [Entity.account] : (entity: CTCEntity): string => {
    return entityId(entity)?.toString();
  }
};

/**
 * defaultRuleValidator defines a default rule validator for entity labels
 * when no custom rules are passed into from initialization.
 *
 * This default method first check @param role for restricted roles.
 * If unrestricted role, return the default @param label.
 * If restricted role, utilize the entity object + entity type to return
 * a resolved label.
 *
 * @params ValidatorParams
 * @returns string
 */
export const defaultRuleValidator = ({
  entity,
  type,
  label,
  role
}: ValidatorParams): string => {
  if (isRestricted(role)) {
    return resolvers[type]?.(entity) || label;
  }
  return label;
};
